import { Component } from '@theme/component';
import { debounce, isMobileBreakpoint } from '@theme/utilities';

class ScrollingCards extends Component {
  /** @type {HTMLElement[]} */
  #cardElements = [];

  /** @type {number} */
  #cardCount = 0;

  /** @type {Animation[]} */
  #timelineAnimations = [];

  /** @type {AbortController | null} */
  #scrollListenerAbort = null;

  /** @type {number | null} */
  #scrollRafId = null;

  /** @type {IntersectionObserver | null} */
  #inViewObserver = null;

  /** @type {boolean} */
  #isInView = true;

  /** @type {boolean} */
  #isAnimatingZone = false;

  /** @type {(() => void) | null} */
  #scheduleUpdate = null;

  /** @type {WeakMap<HTMLElement, string>} */
  #lastTransformByCard = new WeakMap();

  // Layout cache — computed once in #setupScrollFallback() and on resize
  #cardTop = 0;
  #cardHeight = 0;
  #windowHeight = 0;
  #cachedSectionTopDocPx = 0;
  #cachedElementHeight = 0;

  /** Whether we've entered the animation zone and refreshed the position cache. */
  #animationZoneEntered = false;

  #handleResize = debounce(() => {
    for (const card of this.querySelectorAll('.scrolling-card')) {
      card.style.removeProperty('transform');
    }
    this.style.removeProperty('padding-bottom');
    if (this.#scrollRafId != null) cancelAnimationFrame(this.#scrollRafId);
    this.#scrollRafId = null;
    this.#init();
  }, 150);

  connectedCallback() {
    super.connectedCallback();
    requestAnimationFrame(() => this.#init());
    window.addEventListener('resize', this.#handleResize, { signal: this.abortController.signal });
  }

  updatedCallback() {
    super.updatedCallback();
    requestAnimationFrame(() => this.#init());
  }

  disconnectedCallback() {
    this.#cancelTimelineAnimations();
    this.#scrollListenerAbort?.abort();
    this.#scrollListenerAbort = null;
    this.#inViewObserver?.disconnect();
    this.#inViewObserver = null;
    this.#handleResize.cancel();
    if (this.#scrollRafId != null) cancelAnimationFrame(this.#scrollRafId);
    this.#scrollRafId = null;
    this.#scheduleUpdate = null;
    this.style.removeProperty('padding-bottom');
    super.disconnectedCallback();
  }

  #init() {
    this.#cancelTimelineAnimations();
    this.#scrollListenerAbort?.abort();
    this.#scrollListenerAbort = null;

    this.#cardElements = [...this.querySelectorAll('.scrolling-card')];
    this.#cardCount = this.#cardElements.length;

    if (!this.#cardCount) return;

    if (this.#isStickyDisabledOnMobile()) {
      this.#resetCards();
      return;
    }

    if (this.#isViewTimelineAvailable()) {
      try {
        this.#setupViewTimelineAnimations();
        return;
      } catch {
        /* fall through to scroll fallback */
      }
    }

    this.#setupScrollFallback();
  }

  #isStickyDisabledOnMobile() {
    if (this.dataset.productVisualPreview === 'true' && window.matchMedia('(max-width: 1024px)').matches) return true;
    return this.dataset.stickyMobile === 'false' && isMobileBreakpoint();
  }

  #resetCards() {
    for (const card of this.#cardElements) {
      card.style.removeProperty('transform');
      card.style.removeProperty('z-index');
    }
    this.style.removeProperty('padding-bottom');
    this.#lastTransformByCard = new WeakMap();
  }

  #isViewTimelineAvailable() {
    return typeof ViewTimeline !== 'undefined';
  }

  #setupViewTimelineAnimations() {
    const totalCards = this.#cardCount;
    if (!totalCards) return;

    const timeline = new ViewTimeline({ subject: this, axis: 'block' });

    this.#cardElements.forEach((card, cardIndex) => {
      const oneBasedSlot = cardIndex + 1;
      const trailingCardCount = totalCards - oneBasedSlot;
      const startPct = (cardIndex / totalCards) * 100;
      const endPct = (oneBasedSlot / totalCards) * 100;

      const animation = card.animate(
        {
          transform: ['scale(1)', `scale(${1 - 0.1 * trailingCardCount})`],
        },
        {
          timeline,
          fill: 'forwards',
          rangeStart: `exit-crossing ${startPct}%`,
          rangeEnd: `exit-crossing ${endPct}%`,
        }
      );
      this.#timelineAnimations.push(animation);
    });
  }

  #cancelTimelineAnimations() {
    for (const animation of this.#timelineAnimations) {
      try {
        animation.cancel();
      } catch {
        /* ignore */
      }
    }
    this.#timelineAnimations = [];
  }

  #setupScrollFallback() {
    this.#windowHeight = window.innerHeight;

    if (this.#cardElements[0]) {
      const cardStyle = getComputedStyle(this.#cardElements[0]);
      this.#cardTop = Math.floor(parseFloat(cardStyle.getPropertyValue('top'))) || 0;
      this.#cardHeight = Math.floor(this.#cardElements[0].offsetHeight) || 0;
    }

    this.#cachedElementHeight = this.offsetHeight;
    const rect = this.getBoundingClientRect();
    this.#cachedSectionTopDocPx = rect.top + window.scrollY;

    this.#cardElements.forEach((card, i) => {
      this.#setCardTransform(card, 'translateZ(0) scale(1)');
      card.style.zIndex = String(i + 1);
    });

    this.#animationZoneEntered = false;
    this.#isAnimatingZone = false;
    this.#setupInViewObserver();
    this.#scrollListenerAbort = new AbortController();
    this.#bindScrollFallback(this.#scrollListenerAbort.signal);
  }

  #setupInViewObserver() {
    this.#inViewObserver?.disconnect();
    this.#inViewObserver = new IntersectionObserver(
      (entries) => {
        this.#isInView = !!entries[0]?.isIntersecting;
        if (this.#isInView) this.#scheduleUpdate?.();
      },
      { threshold: 0, rootMargin: '50% 0px' }
    );
    this.#inViewObserver.observe(this);
  }

  #bindScrollFallback(signal) {
    const scheduleUpdate = () => {
      if (!this.#isInView) return;
      if (this.#scrollRafId != null) return;
      this.#scrollRafId = requestAnimationFrame(() => {
        this.#scrollRafId = null;
        this.#updateTransforms();
      });
    };
    this.#scheduleUpdate = scheduleUpdate;
    scheduleUpdate();
    window.addEventListener('scroll', scheduleUpdate, { passive: true, signal });
  }

  #setCardTransform(card, transform) {
    if (this.#lastTransformByCard.get(card) === transform) return;
    card.style.transform = transform;
    this.#lastTransformByCard.set(card, transform);
  }

  #updateTransforms() {
    if (!this.#isInView) return;
    const scrollY = window.scrollY;
    let top = this.#cachedSectionTopDocPx - scrollY;

    const guard =
      this.#cardTop - top +
      this.#windowHeight -
      this.#cachedElementHeight -
      this.#cardHeight;

    if (guard > 0) {
      if (this.#animationZoneEntered) this.#animationZoneEntered = false;
      if (!this.#isAnimatingZone) return;
      this.#isAnimatingZone = false;
      this.#cardElements.forEach((card) => this.#setCardTransform(card, 'translateZ(0) scale(1)'));
      return;
    }

    this.#isAnimatingZone = true;

    if (!this.#animationZoneEntered) {
      this.#animationZoneEntered = true;
      const rect = this.getBoundingClientRect();
      this.#cachedSectionTopDocPx = rect.top + scrollY;
      top = rect.top;
    }

    const lastIndex = this.#cardCount - 1;
    for (let i = 0; i < this.#cardCount; i++) {
      const card = this.#cardElements[i];
      const scrolling = this.#cardTop - top - i * this.#cardHeight;
      const base = 'translateZ(0)';
      if (scrolling > 0) {
        const scaling = i === lastIndex ? 1 : (this.#cardHeight - scrolling * 0.15) / this.#cardHeight;
        const minScale = i === lastIndex ? 1 : 1 - 0.1 * (lastIndex - i);
        const clampedScale = Math.max(minScale, Math.min(1, scaling));
        this.#setCardTransform(card, `${base} scale(${clampedScale})`);
      } else {
        this.#setCardTransform(card, `${base} scale(1)`);
      }
    }
  }
}

if (!customElements.get('scrolling-cards')) {
  customElements.define('scrolling-cards', ScrollingCards);
}

// QUICK_GUIDE_PRODUCT_VISUALS_BEGIN
const quickGuideProducts = [
  {
    title: 'Luna Plateada 16',
    direction: 'شرقي دافئ · فاكهي غورماند · أزهار بيضاء',
    choice: 'اختيارك عندما تريد اجتماع الدفء الشرقي والحلاوة مع الأزهار البيضاء في عطر واحد.',
    image: 'https://cdn.shopify.com/s/files/1/0705/6320/1137/files/dos-lunas-luna-plateada-16-beige-studio.png?v=1791304341',
    url: '/products/dos-lunas-luna-plateada-16-eau-de-parfum-50ml',
  },
  {
    title: 'Atractivo 14',
    direction: 'شرقي خشبي زهري',
    choice: 'اختره إذا كنت تميل إلى دفء الشرق وعمق الأخشاب مع حضور زهري متوازن.',
    image: 'https://cdn.shopify.com/s/files/1/0705/6320/1137/files/dos-lunas-atractivo-14-beige-studio.png?v=1791304439',
    url: '/products/dos-lunas-atractivo-14-eau-de-parfum-50ml',
  },
  {
    title: 'Elegante 91',
    direction: 'زهري منعش · فواكه عصيرية',
    choice: 'اختره إذا كنت تميل إلى طابع زهري منعش تتقدمه الفواكه العصيرية.',
    image: 'https://cdn.shopify.com/s/files/1/0705/6320/1137/files/dos-lunas-elegante-91-blue-studio.png?v=1791304290',
    url: '/products/dos-lunas-elegante-91-eau-de-parfum-50ml',
  },
  {
    title: 'Sejuro 61',
    direction: 'خشبي · بحري · عنبري',
    choice: 'اختره إذا كنت تريد الخروج من اتجاه الحلاوة الشرقية نحو مسار أكثر اختلافًا.',
    image: 'https://cdn.shopify.com/s/files/1/0705/6320/1137/files/dos-lunas-sejuro-61-blue-studio.png?v=1791304289',
    url: '/products/dos-lunas-sejuro-61-eau-de-parfum-50ml',
  },
  {
    title: 'Luna Flore 28',
    direction: 'عنبري زهري · لمسات خضراء',
    choice: 'اختره إذا كنت تميل إلى الزهور الأنيقة مع لمسة خضراء منعشة ودفء العنبر.',
    image: 'https://cdn.shopify.com/s/files/1/0705/6320/1137/files/dos-lunas-luna-flore-28-floral-studio.png?v=1791304289',
    url: '/products/dos-lunas-luna-flore-28-eau-de-parfum-50ml',
  },
  {
    title: 'Luna Liena 78',
    direction: 'فاكهي غورماند · زهري ناعم · مسكي',
    choice: 'اختره إذا كنت تميل إلى الحلاوة الفاكهية مع أزهار ناعمة ولمسة مسكية دافئة.',
    image: 'https://cdn.shopify.com/s/files/1/0705/6320/1137/files/dos-lunas-luna-liena-78-pink-studio.png?v=1791304340',
    url: '/products/dos-lunas-luna-liena-78-eau-de-parfum-50ml',
  },
];

function ensureQuickGuideStyles() {
  if (document.getElementById('quick-guide-product-visuals')) return;

  const style = document.createElement('style');
  style.id = 'quick-guide-product-visuals';
  style.textContent = `
    scrolling-cards[data-product-visual-preview="true"] .scrolling-cards_items {
      gap: 34px !important;
    }

    scrolling-cards[data-product-visual-preview="true"] .scrolling-card > .group-block {
      padding: 0 !important;
    }

    .quick-guide-product-card {
      display: grid;
      grid-template-columns: minmax(0, 1.04fr) minmax(320px, 0.96fr);
      align-items: stretch;
      width: min(100%, 1180px);
      min-height: 430px;
      margin-inline: auto;
      overflow: hidden;
      border-radius: 28px;
      background: var(--color-background, #fff);
      text-align: start;
      box-shadow: 0 10px 34px rgba(20, 18, 15, 0.045);
    }

    .quick-guide-product-card__media {
      display: block;
      min-width: 0;
      min-height: 0;
      aspect-ratio: 1 / 1;
      overflow: hidden;
      background: rgba(0, 0, 0, 0.025);
    }

    .quick-guide-product-card__media img {
      display: block;
      width: 100%;
      height: 100%;
      min-height: 0;
      max-height: none;
      object-fit: contain;
      object-position: center;
    }

    .quick-guide-product-card__content {
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: flex-start;
      gap: 13px;
      min-width: 0;
      padding: clamp(34px, 4.5vw, 64px);
    }

    .quick-guide-product-card__direction {
      margin: 0;
      max-width: 34rem;
      font-size: clamp(0.92rem, 1.15vw, 1.05rem);
      line-height: 1.75;
      opacity: 0.72;
    }

    .quick-guide-product-card__title {
      margin: 0;
      max-width: 34rem;
      font-family: var(--font-heading-family, inherit);
      font-size: clamp(1.9rem, 3.2vw, 3rem);
      line-height: 1.12;
      overflow-wrap: anywhere;
    }

    .quick-guide-product-card__choice {
      margin: 4px 0 0;
      max-width: 34rem;
      font-size: clamp(1rem, 1.25vw, 1.13rem);
      line-height: 1.9;
      opacity: 0.9;
    }

    @media (max-width: 1024px) {
      scrolling-cards[data-product-visual-preview="true"] .scrolling-card {
        position: static !important;
        top: auto !important;
        transform: none !important;
        will-change: auto !important;
      }

      scrolling-cards[data-product-visual-preview="true"] .scrolling-cards_items {
        gap: 22px !important;
      }
    }

    @media (max-width: 989px) {
      .quick-guide-product-card {
        grid-template-columns: minmax(0, 1fr) minmax(280px, 0.9fr);
        min-height: 390px;
      }

      .quick-guide-product-card__media,
      .quick-guide-product-card__media img {
        min-height: 0;
      }

      .quick-guide-product-card__content {
        padding: 34px 30px;
      }
    }

    @media (max-width: 767px) {
      scrolling-cards[data-product-visual-preview="true"] .scrolling-cards_items {
        gap: 22px !important;
      }

      .quick-guide-product-card {
        grid-template-columns: 1fr;
        min-height: 0;
        border-radius: 22px;
      }

      .quick-guide-product-card__media {
        min-height: 0;
        aspect-ratio: 1 / 1;
      }

      .quick-guide-product-card__media img {
        min-height: 0;
        height: 100%;
        max-height: none;
        object-position: center;
      }

      .quick-guide-product-card__content {
        gap: 10px;
        padding: 22px 20px 26px;
      }

      .quick-guide-product-card__direction {
        font-size: 0.9rem;
        line-height: 1.65;
      }

      .quick-guide-product-card__title {
        font-size: clamp(1.55rem, 8vw, 2rem);
        line-height: 1.18;
      }

      .quick-guide-product-card__choice {
        margin-top: 2px;
        font-size: 0.98rem;
        line-height: 1.8;
      }
    }
  `;
  document.head.append(style);
}

function buildQuickGuideCard(templateCard, product) {
  const card = templateCard.cloneNode(false);
  card.removeAttribute('id');
  [...card.attributes].forEach((attribute) => {
    if (attribute.name.startsWith('data-shopify-editor')) card.removeAttribute(attribute.name);
  });

  const shell = document.createElement('div');
  shell.className = 'group-block quick-guide-product-card';
  shell.innerHTML = `
    <a class="quick-guide-product-card__media" href="${product.url}" aria-label="${product.title}">
      <img src="${product.image}" alt="${product.title}" loading="lazy" width="960" height="720">
    </a>
    <div class="quick-guide-product-card__content">
      <p class="quick-guide-product-card__direction">${product.direction}</p>
      <h3 class="quick-guide-product-card__title">${product.title}</h3>
      <p class="quick-guide-product-card__choice">${product.choice}</p>
    </div>
  `;
  card.append(shell);
  return card;
}

function mountQuickGuideProductVisuals() {
  document.querySelectorAll('scrolling-cards').forEach((section) => {
    if (!section.textContent.includes('دليل اختيار سريع')) return;
    if (section.dataset.productVisualPreview === 'true') return;

    const items = section.querySelector('.scrolling-cards_items');
    const templateCard = items?.querySelector('.scrolling-card');
    if (!items || !templateCard) return;

    ensureQuickGuideStyles();
    section.dataset.productVisualPreview = 'true';

    const fragment = document.createDocumentFragment();
    for (const product of quickGuideProducts) {
      fragment.append(buildQuickGuideCard(templateCard, product));
    }
    items.replaceChildren(fragment);

    requestAnimationFrame(() => window.dispatchEvent(new Event('resize')));
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', mountQuickGuideProductVisuals, { once: true });
} else {
  mountQuickGuideProductVisuals();
}
// QUICK_GUIDE_PRODUCT_VISUALS_END
